import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import { faUniversity } from '@fortawesome/pro-regular-svg-icons'
import { PayrollSetupList } from './PayrollSetupList'
import {
  postCreateEmployeeBankAccount,
  fetchEmployeeBankAccounts,
  fetchEmployeePaymentMethod,
  fetchEmployees,
  putUpdateEmployeePaymentMethod,
  deleteEmployeeBankAccount,
  fetchAllEmployeeForms,
  POST_CREATE_EMPLOYEE_BANK_ACCOUNT_KEY,
  PUT_UPDATE_EMPLOYEE_PAYMENT_METHOD_KEY,
} from '../payrollActions'
import { selectFirstEmployee } from '../payroll.selectors'
import { GustoFormsTable } from '../payroll-documents/GustoFormsTable'
import {
  GEP_ENROLL_PATHS,
  ONBOARDING_STEPS,
  useUpdatePayrollOnboardingStep,
} from '../helpers'
import { PrefilledNoticeContainer } from './PrefilledNoticeContainer'
import { PayrollError } from '../PayrollError'
import { Button, Card, Icon, Text, Form, Separator, Dialog } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { getIsFetching } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  Gusto_EmployeeBankAccount,
  Gusto_GustoForm,
} from '../generated_gusto_types'
import { TextColors, TypographyStyles } from '@/styles'
import { useAnalyticsTrack } from '@/features/amplitude'

// TODO: Migrate to V2
import { makeNumberSchema, makeReqStringSchema } from '@/base'

// Dev note - Routing number 102001017 and account number 01234567 pass gusto validation

const validationSchema = yup.object({
  routingNumber: makeNumberSchema({
    field: 'routing number',
    numDigits: 9,
    allowLeadingZero: true,
  }),
  accountNumber: makeNumberSchema({
    field: 'account number',
    allowLeadingZero: true,
  }),
  accountType: makeReqStringSchema({ field: 'account type' }),
})

const BankAccountModal = ({
  employeeUuid,
  onClose,
  isOpen,
}: {
  employeeUuid: string
  onClose: () => void
  isOpen: boolean
}) => {
  const dispatch = useAppDispatch()
  const isSaving = useReselector(
    getIsFetching,
    POST_CREATE_EMPLOYEE_BANK_ACCOUNT_KEY
  )

  const formik = useFormik({
    initialValues: {
      routingNumber: '',
      accountNumber: '',
      accountType: 'Checking' as 'Checking' | 'Savings',
    },
    validationSchema,
    onSubmit: async ({ routingNumber, accountNumber, accountType }) => {
      const bankAccountRes = await postCreateEmployeeBankAccount(employeeUuid, {
        name: 'Bank Account',
        routing_number: routingNumber,
        account_type: accountType,
        account_number: accountNumber,
      })(dispatch)

      if (bankAccountRes) {
        onClose()
      }
    },
  })

  return (
    <Dialog open={isOpen} onOpenChange={onClose} onClose={onClose}>
      <Dialog.Content>
        <Dialog.Title>Add Personal Bank Account</Dialog.Title>

        <FormikProvider value={formik}>
          <div className="flex flex-col gap-4 mt-2">
            <PayrollError fetchKey={POST_CREATE_EMPLOYEE_BANK_ACCOUNT_KEY} />

            <Form.Input
              name="routingNumber"
              label="Routing Number (9 digits)"
              required
            />

            <Form.Input name="accountNumber" label="Account Number" required />

            <Form.Select
              name="account_type"
              options={['Checking', 'Savings'].map((type) => ({
                text: type,
                value: type,
              }))}
              required
              label="Account Type"
            />
          </div>
        </FormikProvider>

        <Dialog.Footer className="pt-4 flex justify-end gap-2">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={formik.submitForm}
            loading={isSaving}
            disabled={isSaving}
          >
            Save
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

const BankForm = ({ employeeUuid }: { employeeUuid: string }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()

  const [paymentMethodVersion, setPaymentMethodVersion] = useState<string>()
  const [bankAccount, setBankAccount] = useState<Gusto_EmployeeBankAccount>()
  const [bankAccountFetched, setBankAccountFetched] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [forms, setForms] = useState<Gusto_GustoForm[]>([])
  const isPosting = useReselector(
    getIsFetching,
    PUT_UPDATE_EMPLOYEE_PAYMENT_METHOD_KEY
  )
  useUpdatePayrollOnboardingStep(ONBOARDING_STEPS.employeeBank)

  const fetchBankAccount = useCallback(async () => {
    const bankAccountRes =
      await fetchEmployeeBankAccounts(employeeUuid)(dispatch)

    if (bankAccountRes) {
      setBankAccountFetched(true)

      if (bankAccountRes.length) {
        setBankAccount(bankAccountRes[0])
      } else {
        setBankAccount(undefined)
      }
    }
  }, [dispatch, employeeUuid])

  const fetchForms = useCallback(async () => {
    const res = await fetchAllEmployeeForms(employeeUuid)(dispatch)
    if (res) {
      setForms(res)
    }
  }, [dispatch, employeeUuid])

  // Bank account fetch
  useEffect(() => {
    const fetch = async () => {
      await fetchBankAccount()

      if (!bankAccount?.uuid) {
        return
      }

      const res = await fetchEmployeePaymentMethod(employeeUuid)(dispatch)

      if (res) {
        setPaymentMethodVersion(res.version)
      }
    }
    fetch()
  }, [bankAccount?.uuid, dispatch, employeeUuid, fetchBankAccount])

  // Forms fetch
  useEffect(() => {
    fetchForms()
  }, [fetchForms])

  const submit = useCallback(async () => {
    if (!paymentMethodVersion || !bankAccount?.uuid) {
      return
    }

    const paymentMethodRes = await putUpdateEmployeePaymentMethod(
      employeeUuid,
      {
        type: 'Direct Deposit',
        version: paymentMethodVersion,
        split_by: 'Percentage',
        splits: [
          {
            uuid: bankAccount.uuid,
            name: bankAccount.name,
            priority: 1,
            split_amount: 100,
          },
        ],
      }
    )(dispatch)

    if (!paymentMethodRes) {
      return
    }

    track(
      'completed payroll step - create or update employee bank account and sign forms'
    )

    navigate(GEP_ENROLL_PATHS.schedule)
  }, [
    paymentMethodVersion,
    bankAccount?.uuid,
    bankAccount?.name,
    employeeUuid,
    dispatch,
    track,
    navigate,
  ])

  const deleteBankAccount = useCallback(async () => {
    if (!bankAccount?.uuid) {
      return
    }

    const res = await deleteEmployeeBankAccount(
      employeeUuid,
      bankAccount?.uuid
    )(dispatch)

    if (res) {
      await fetchBankAccount()
    }
  }, [bankAccount?.uuid, dispatch, employeeUuid, fetchBankAccount])

  const onClose = useCallback(() => {
    fetchBankAccount()
    // Forms are refetched here because there may be a new one to sign after bank added
    fetchForms()
    setModalOpen(false)
  }, [fetchBankAccount, fetchForms])

  if (!bankAccountFetched) {
    return null
  }

  // Form used for styling
  return (
    <div className="flex flex-col gap-4">
      <PayrollError fetchKey={PUT_UPDATE_EMPLOYEE_PAYMENT_METHOD_KEY} />

      <Text variant={TypographyStyles.H2} className="mt-4">
        Add yourself as an employee
      </Text>
      <Separator />
      <Text variant={TypographyStyles.H2}>
        Enter Employee Payment Information
      </Text>
      <Text variant={TypographyStyles.BODY_SM}>
        We will use Direct Deposit for your payment. Add the details of the
        personal bank account where you’d like to receive payment.
      </Text>

      <BankAccountModal
        employeeUuid={employeeUuid}
        onClose={onClose}
        isOpen={modalOpen}
      />

      {!bankAccount && bankAccountFetched && (
        <div className="flex flex-col gap-2">
          <Text variant={TypographyStyles.BODY_LG}>Employee Bank Account</Text>
          <Text>
            Please connect your <b>personal</b> bank account.
          </Text>
          <div className="w-full lg:w-3/4">
            <Button onClick={() => setModalOpen(true)}>Add Bank Account</Button>
          </div>
        </div>
      )}

      {bankAccount && bankAccountFetched && (
        <div className="w-full lg:w-3/4 border border-border rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between">
            <Text color={TextColors.GREEN}>
              <b>
                <Icon icon={faUniversity} className="mr-2" />
                {bankAccount.account_type} Account
              </b>
            </Text>
            <Button onClick={deleteBankAccount} variant="link">
              Delete
            </Button>
          </div>
          <Text className="mb-0">{bankAccount.routing_number}</Text>
          <Text>
            <b>{bankAccount.hidden_account_number}</b>
          </Text>
        </div>
      )}

      <Separator />

      <Text variant={TypographyStyles.H2}>Sign Documents</Text>
      <GustoFormsTable
        forms={forms}
        employeeUuid={employeeUuid}
        allowSign
        onSign={fetchForms}
      />

      <div className="flex flex-col lg:flex-row gap-4 pt-4">
        <div className="w-full lg:w-1/3">
          <Button
            variant="secondary"
            onClick={() => navigate(GEP_ENROLL_PATHS.employeeState)}
          >
            Back
          </Button>
        </div>
        <div className="w-full lg:w-1/3 lg:ml-auto">
          <Button
            onClick={submit}
            disabled={
              forms.some((form) => form.requires_signing) || !bankAccount
            }
            loading={isPosting}
          >
            Save & Continue
          </Button>
        </div>
      </div>
    </div>
  )
}

export const PayrollEnrollBank = () => {
  const dispatch = useAppDispatch()
  const employee = useReselector(selectFirstEmployee)

  useEffect(() => {
    fetchEmployees()(dispatch)
  }, [dispatch])

  return (
    <div>
      <Card>
        <Text variant={TypographyStyles.DISPLAY_XS}>Setting Up Payroll</Text>
        <Separator />
        <PrefilledNoticeContainer />

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:w-3/5">
            {employee && <BankForm employeeUuid={employee.uuid} />}
          </div>
          <div className="w-full lg:w-2/5">
            <PayrollSetupList />
          </div>
        </div>
      </Card>
    </div>
  )
}
